// ═══════════════════════════════════════════════════════════════════
//  APPOINTMENTS → SEARCH BY SOCIAL SECURITY NUMBER (#771)
//
//  The number travels in one POST body; it is not kept after the call
//  is dispatched:
//  - never in a URL or query string (the endpoint is a POST);
//  - never in a query's cache key: it goes through the
//    `searchBookingRequestsBySsn` mutation, not the search query;
//  - never in the store afterwards: the mutation records its arguments
//    while it runs, so the result is taken and the mutation reset at once,
//    which drops them;
//  - never in component state: the mutation is dispatched directly
//    (`endpoints.searchBookingRequestsBySsn.initiate`), never through its
//    generated hook, which would keep the request as `originalArgs` in the
//    calling component's state (searchApi.ts does not export that hook);
//  - never in an error message, the console or storage: a failure is
//    turned into a fixed sentence here, and nothing is logged;
//  - never in the form afterwards: the page clears the field as it sends.
//
//  The results are one page (SSN_SEARCH_TAKE) and are not refetched, since
//  either would mean sending the number again.
// ═══════════════════════════════════════════════════════════════════

import type { BookingSearchPage, BookingSsnSearchRequest } from "./searchApi"
import { searchErrorMessage } from "./searchApi"

/** What a search by Social Security number came to. Holds no part of the number. */
export type SsnSearchOutcome =
  | { state: "loading" }
  | { state: "ready"; data: BookingSearchPage }
  | { state: "error"; message: string }

/**
 * What dispatching the mutation's `initiate` hands back: RTK Query's MutationActionCreatorResult, as far
 * as used here. Not the generated mutation hook's trigger — see searchApi.ts.
 */
export interface SsnSearchCall {
  unwrap(): Promise<BookingSearchPage>
  reset(): void
}

/** Why a search by Social Security number failed, in words that never quote it. */
export function ssnSearchErrorMessage(error: unknown): string {
  const failure = error as { status?: unknown } | undefined
  if (failure?.status === 403) return "You do not have permission to search by Social Security number."
  // The server's 400 messages never quote the number; anything else is a fixed sentence.
  return searchErrorMessage(error)
}

/**
 * Sends one search by Social Security number and resets the mutation as soon as it settles, so the
 * request it recorded — the number included — leaves the store with it. The outcome carries the
 * results or a message, never the request.
 */
export async function runSsnSearch(
  body: BookingSsnSearchRequest,
  send: (body: BookingSsnSearchRequest) => SsnSearchCall,
): Promise<Exclude<SsnSearchOutcome, { state: "loading" }>> {
  const call = send(body)
  try {
    return { state: "ready", data: await call.unwrap() }
  } catch (error) {
    return { state: "error", message: ssnSearchErrorMessage(error) }
  } finally {
    call.reset()
  }
}
